import { AlertTriangle, FileSignature, Loader2 } from 'lucide-react';
import { lazy, Suspense, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useApiCatalogue } from '@/features/workflows/api/useApiCatalogue';
import { useWorkflow } from '@/features/workflows/api/useWorkflow';
import type { WorkflowStepDto } from '@/features/workflows/schemas/workflowSchemas';

import { useTemplates } from '../api/useTemplates';
import { findEsignSource } from '../lib/esignSource';
import {
  type EsignSigner,
  esignOperationId,
  parseEsignStepSettings,
  sameEsignSigners,
  signTypeLabel,
  writeEsignStepSettings,
} from '../lib/esignStepSettings';

// The ONLY import of the Word editor in the workflow editor's reach, and it is
// lazy: placing a signature needs the document's pages rendered, which costs
// megabytes that most workflows never load.
const EsignPlacement = lazy(() =>
  import('./EsignPlacement').then((m) => ({ default: m.EsignPlacement })),
);

interface EsignStepPanelProps {
  step: WorkflowStepDto;
  onUpdate: (patch: Partial<WorkflowStepDto>) => void;
  readOnly?: boolean;
}

/**
 * What an eSign Template step shows instead of the verification tabs.
 *
 * It signs the document produced by the Document Template step before it. Signers
 * and their signature positions are set on a dedicated screen, because placing a
 * signature means seeing the page it goes on — which needs the document rendered,
 * and that pulls in the editor bundle.
 */
export function EsignStepPanel({ step, onUpdate, readOnly = false }: EsignStepPanelProps) {
  const { workflowId } = useParams<{ workflowId: string }>();

  const { data: workflow } = useWorkflow(workflowId);
  const { data: templates } = useTemplates();
  const { data: catalogue } = useApiCatalogue('eSign API Suite');

  const settings = parseEsignStepSettings(step.settings);
  const source = findEsignSource(workflow?.steps ?? [], step.id);
  const template = templates?.find((t) => t.templateId === source?.templateId);

  // Price per signature METHOD, straight from the catalogue — an Aadhaar signature
  // and an electronic one are different products at different prices.
  const priceByOperationId = new Map(
    (catalogue ?? [])
      .filter((entry) => entry.costPerCall != null)
      .map((entry) => [entry.operationId, entry.costPerCall as number]),
  );

  /**
   * Mirror the signature editor's signer list onto the step, so the step list and the
   * session total price what is on screen rather than what was last saved.
   *
   * Compared before writing: this fires on every edit, and an unconditional update
   * would mark the workflow dirty just for opening the step — and loop, since the
   * parent re-renders this component with new settings.
   */
  const handleSignersChange = useCallback(
    (signers: EsignSigner[]) => {
      const current = parseEsignStepSettings(step.settings).signers;
      if (sameEsignSigners(current, signers)) return;

      onUpdate({ settings: writeEsignStepSettings(step.settings, { signers }) });
    },
    [step.settings, onUpdate],
  );

  return (
    /*
      -m-4 undoes the settings card's padding so the sticky header and footer can
      span its full width — a bar inset from the edges reads as a floating box
      rather than as chrome.
    */
    <div className="-m-4 flex h-full flex-col">
      {/* A fixed rail of the flex column — the panel fills its area and does not
          scroll, so this stays put without needing position:sticky.
          One row, not two stacked blocks: the title needs a third of the width and
          the rest was empty. The document being signed sits opposite it. */}
      <header className="flex shrink-0 items-center gap-4 border-b border-grayEF bg-white px-4 py-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold tracking-tight text-black-logo">Signatures</h3>
          <p className="mt-0.5 text-xs text-gray66">
            Who signs the generated document, and where each signature goes.
          </p>
        </div>

        {source ? (
          <div className="ms-auto flex min-w-0 max-w-[52%] items-center gap-2.5 rounded-lg bg-grayEF/50 px-3 py-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <FileSignature className="h-4 w-4 text-primary" aria-hidden="true" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium text-black-logo">
                {template?.templateName ?? 'Attached template'}
              </span>
              <span className="block truncate text-[11px] text-gray66">
                Generated by the Document Template step, then signed here.
              </span>
            </span>
          </div>
        ) : null}
      </header>

      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4 pt-4">
        {/* No document to sign — the one thing that makes this step meaningless. */}
        {!source ? (
          <div className="flex items-start gap-3 rounded-lg border border-orangeDA/30 bg-orangeDA/5 p-4">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orangeDA" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium text-black-logo">No document to sign</p>
              <p className="mt-1 text-xs leading-relaxed text-gray66">
                Add a Document Template step before this one and attach a template to it. This step
                signs whatever that step generates.
              </p>
            </div>
          </div>
        ) : readOnly ? (
          <div className="space-y-2 rounded-lg border border-grayEF p-3">
            <p className="text-xs font-medium text-gray30">Signers</p>
            {settings.signers.length === 0 ? (
              <p className="text-xs text-gray66">No signers configured.</p>
            ) : (
              <ul className="space-y-1.5">
                {settings.signers.map((signer, index) => (
                  <li key={signer.userId} className="flex items-center gap-2 text-xs">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-white">
                      {index + 1}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-black-logo">{signer.name}</span>
                    <span className="shrink-0 text-gray66">{signTypeLabel(signer.signType)}</span>
                    <span className="w-14 shrink-0 text-right font-medium text-gray30">
                      ₹{' '}
                      {(priceByOperationId.get(esignOperationId(signer.signType)) ?? 0).toFixed(2)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          /* The document and its signature positions, in place — placing a signature
             means seeing the page it lands on. */
          <Suspense
            fallback={
              <div className="flex min-h-0 flex-1 items-center justify-center gap-2 rounded-lg border border-grayEF">
                <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
                <span className="text-sm text-gray66">Loading the signature editor…</span>
              </div>
            }
          >
            <EsignPlacement
              templateId={source.templateId}
              priceByOperationId={priceByOperationId}
              savedSigners={settings.signers}
              onSignersChange={handleSignersChange}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
}
